import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { AccessTime, Business, Person, Event, Today, Refresh } from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfDay, isToday as isTodayFn } from 'date-fns';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';
import { apiService } from '../services/apiService';

const IST_TIMEZONE = 'Asia/Kolkata';

export interface CalendarSession {
  _id: string;
  className: string;
  date: string;
  slots: number;
  availableSlots: number;
  price: number;
  duration: number;
  image?: string;
  sessionType?: 'group' | 'private' | 'duet';
  isTrialEnabled?: boolean;
  trialPrice?: number;
  studio: {
    _id: string;
    studioName: string;
    address?: string;
    whitelabelConfig?: { appSource?: string; appName?: string };
  };
  instructor: {
    _id: string;
    name: string;
    image?: string;
  };
  classType: {
    _id: string;
    name: string;
  };
  bookingCount?: number;
}

interface BookingsCalendarViewProps {
  onSelectSession: (session: CalendarSession) => void;
}

// Reuses the same calendar + per-day session-card pattern as ActiveClassesPage,
// but shows ALL sessions for a date (past included, for reviewing historical
// bookings) and clicking a session filters the bookings list instead of
// navigating to the class or offering delete actions.
const BookingsCalendarView: React.FC<BookingsCalendarViewProps> = ({ onSelectSession }) => {
  const getCurrentISTDate = () => startOfDay(toZonedTime(new Date(), IST_TIMEZONE));

  const [selectedDate, setSelectedDate] = useState<Date>(getCurrentISTDate());
  const [sessions, setSessions] = useState<CalendarSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionFilter, setSessionFilter] = useState<'with_bookings' | 'all'>('with_bookings');

  // Sessions rarely change while an admin is browsing, so once a date has been
  // fetched we serve it from here instead of hitting the API again when the
  // admin flips back to a date they already visited (e.g. 13 -> 12 -> 13).
  // The refresh button bypasses this when they actually want fresh data.
  const sessionsCacheRef = useRef<Map<string, CalendarSession[]>>(new Map());

  const istDateStr = useCallback((date: Date) => {
    const istFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: IST_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return istFormatter.format(date);
  }, []);

  const fetchSessions = useCallback(async (date: Date, forceRefresh = false) => {
    const key = istDateStr(date);
    const cached = sessionsCacheRef.current.get(key);
    if (cached && !forceRefresh) {
      setSessions(cached);
      setError(null);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getAllSessionsByDate({ date: key });
      const list = response.sessions || [];
      sessionsCacheRef.current.set(key, list);
      setSessions(list);
    } catch (err: any) {
      console.error('Error fetching sessions for date:', err);
      setError(err.message || 'Failed to fetch sessions');
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  }, [istDateStr]);

  useEffect(() => {
    fetchSessions(selectedDate);
  }, [selectedDate, fetchSessions]);

  const handleDateSelect = (date: Date | null) => {
    if (!date) return;
    setSelectedDate(date);
  };

  const formatSessionTime = (dateString: string) =>
    formatInTimeZone(new Date(dateString), IST_TIMEZONE, 'hh:mm a');

  const displayedSessions = useMemo(() => {
    if (sessionFilter === 'all') return sessions;
    return sessions.filter((session) => {
      if (session.bookingCount != null) return session.bookingCount > 0;
      // Fallback when API has not yet returned bookingCount (non-cancelled only)
      return session.slots - session.availableSlots > 0;
    });
  }, [sessions, sessionFilter]);

  const renderSessionCard = (session: CalendarSession) => (
    <Card
      key={session._id}
      onClick={() => onSelectSession(session)}
      sx={{
        mb: 2,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {session.image && (
            <Avatar src={session.image} variant="rounded" sx={{ width: 64, height: 64 }}>
              {session.className.charAt(0)}
            </Avatar>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                {session.className}
              </Typography>
              <Chip label={`₹${session.price}`} color="primary" size="small" sx={{ fontWeight: 600 }} />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Chip icon={<AccessTime />} label={`${session.duration} min`} size="small" variant="outlined" />
              <Chip
                icon={<Person />}
                label={`${session.slots - session.availableSlots}/${session.slots} booked`}
                size="small"
                variant="outlined"
                color={session.availableSlots === 0 ? 'error' : 'default'}
              />
              <Chip label={session.classType?.name || 'N/A'} size="small" variant="outlined" />
              <Chip
                icon={<Business />}
                label={session.studio?.studioName || 'N/A'}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontWeight: 600 }}
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Event fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {formatSessionTime(session.date)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Instructor: {session.instructor?.name || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 4 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Paper elevation={2} sx={{ p: 1.5, height: 'fit-content', maxWidth: 350 }}>
            <DateCalendar
              value={selectedDate}
              onChange={handleDateSelect}
              sx={{
                width: '100%',
                '& .MuiPickersCalendarHeader-root': { paddingLeft: 1, paddingRight: 1 },
                '& .MuiDayCalendar-weekDayLabel': { fontSize: '0.75rem', fontWeight: 500 },
                '& .MuiPickersDay-root': { fontSize: '0.875rem' },
              }}
            />
          </Paper>
        </LocalizationProvider>
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        <Paper elevation={2} sx={{ p: 2, height: '100%', maxHeight: '75vh', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight={700}>
              Sessions for {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </Typography>
            {isTodayFn(selectedDate) && (
              <Chip icon={<Today />} label="Today" size="small" color="primary" />
            )}
            <Box sx={{ flex: 1 }} />
            <ToggleButtonGroup
              value={sessionFilter}
              exclusive
              size="small"
              onChange={(_, value: 'with_bookings' | 'all' | null) => {
                if (value) setSessionFilter(value);
              }}
            >
              <ToggleButton value="with_bookings">With bookings</ToggleButton>
              <ToggleButton value="all">All sessions</ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Refresh sessions for this date">
              <span>
                <IconButton size="small" onClick={() => fetchSessions(selectedDate, true)} disabled={isLoading}>
                  <Refresh fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click a session to see its bookings
          </Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : sessions.length === 0 ? (
            <Alert severity="info">No sessions found for the selected date.</Alert>
          ) : displayedSessions.length === 0 ? (
            <Alert severity="info">
              No sessions with bookings for this date. Switch to <strong>All sessions</strong> to include empty ones.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {sessionFilter === 'with_bookings'
                  ? `Showing ${displayedSessions.length} ${displayedSessions.length === 1 ? 'session' : 'sessions'} with bookings`
                  : `Showing ${displayedSessions.length} ${displayedSessions.length === 1 ? 'session' : 'sessions'}`}
              </Typography>
              {displayedSessions.map(renderSessionCard)}
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default BookingsCalendarView;
